import { apiClient } from '../client';

/**
 * SU-6 / SU-7 (W7, ADR-0049): the permission matrix behind permissions_v2
 * and live supervision behind supervision_live
 * (server/src/adapters/inbound/http/permissions.routes.ts, supervision.routes.ts).
 */

export const PERMISSIONS = [
  'records.read', 'records.write', 'telephony.use', 'calls.qualify', 'calls.supervise', 'analytics.read', 'ai.use',
  'email.use', 'calendar.use', 'meetings.book', 'integrations.use', 'contacts.blacklist', 'customers.read',
  'customers.manage', 'members.read', 'members.manage', 'settings.tenant', 'billing.access', 'api_keys.manage',
  'api.docs', 'portal.settings', 'permissions.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];
export type MatrixRole = 'tenant_admin' | 'caller' | 'viewer';
export type Matrix = Record<MatrixRole, Record<string, boolean>>;
export interface W7Flags { permissions_v2: boolean; supervision_live: boolean }

export interface MyPermissions {
  enforced: boolean;
  flags: W7Flags;
  role: MatrixRole | null;
  is_super_admin: boolean;
  permissions: Record<string, boolean>;
}

export interface PermissionMatrix {
  organisation_id: string;
  flags: W7Flags;
  permissions: Permission[];
  roles: MatrixRole[];
  defaults: Matrix;
  effective: Matrix;
  editable: Matrix;
  locked: Array<{ role: MatrixRole; permission: Permission }>;
}

export const getMyPermissions = () => apiClient.get<MyPermissions>('/permissions/me');

export const getPermissionMatrix = (organisationId: string) =>
  apiClient.get<PermissionMatrix>(`/permissions/organisations/${encodeURIComponent(organisationId)}`);

export const updatePermissionMatrix = (organisationId: string, changes: Partial<Record<MatrixRole, Record<string, boolean>>>) =>
  apiClient.patch<PermissionMatrix>(`/permissions/organisations/${encodeURIComponent(organisationId)}`, { changes });

export const setW7Flags = (organisationId: string, patch: Partial<W7Flags>) =>
  apiClient.patch<{ flags: W7Flags }>(`/permissions/organisations/${encodeURIComponent(organisationId)}/flags`, patch);

// ── SU-7 supervision ───────────────────────────────────────────────────────

export type SupervisionMode = 'listen' | 'whisper';

export interface LiveCall {
  call_id: string;
  agent_email: string;
  agent_name: string;
  prospect_name: string;
  connected_at: string | null;
  supervisable: boolean;
  reason: 'no_conference' | null;
  session: { id: string; mode: SupervisionMode } | null;
}

export interface SupervisionSession { id: string; call_id: string; mode: SupervisionMode; started_at: string }

export const listLiveCalls = () => apiClient.get<{ calls: LiveCall[] }>('/supervision/calls');

export const joinCall = (body: { call_id: string; mode: SupervisionMode; phone: string }) =>
  apiClient.post<{ session: SupervisionSession }>('/supervision/sessions', body);

export const switchSupervisionMode = (sessionId: string, mode: SupervisionMode) =>
  apiClient.patch<{ session: SupervisionSession }>(`/supervision/sessions/${encodeURIComponent(sessionId)}`, { mode });

export const leaveCall = (sessionId: string) => apiClient.delete<void>(`/supervision/sessions/${encodeURIComponent(sessionId)}`);
